import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getCurrentUser, getProfile, getAnnouncements } from '../utils/supabase';

const Home = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const { user } = await getCurrentUser();
    setUser(user);
    if (user) {
      const { data } = await getProfile(user.id);
      setProfile(data);
    }
    const { data } = await getAnnouncements(3);
    setAnnouncements(data || []);
    setLoading(false);
  };

  const features = [
    {
      icon: '📋',
      title: 'Daily Todos',
      description: 'Create and manage your todos for the day or future dates',
      link: '/todo',
      color: 'from-blue-500 to-blue-600',
    },
    {
      icon: '📚',
      title: 'Library',
      description: 'See who\'s online and join the community library',
      link: '/library',
      color: 'from-purple-500 to-purple-600',
    },
    {
      icon: '📢',
      title: 'Announcements',
      description: 'Stay updated with the latest announcements',
      link: '/announcements',
      color: 'from-orange-500 to-orange-600',
    },
    {
      icon: '👤',
      title: 'Your Profile',
      description: 'View and manage your profile and todos',
      link: '/profile',
      color: 'from-green-500 to-green-600',
    },
  ];

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] pb-20">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-secondary text-white py-16 px-4">
        <div className="container-custom">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Welcome{profile?.full_name ? `, ${profile.full_name.split(' ')[0]}` : ''}! 👋
          </h1>
          <p className="text-xl opacity-90 max-w-2xl">
            Manage your daily tasks, connect with others, and stay productive together.
          </p>
          {!user && (
            <Link
              to="/auth"
              className="inline-block mt-6 px-6 py-3 bg-white text-primary rounded-lg font-semibold hover:shadow-lg transition"
            >
              Get Started
            </Link>
          )}
        </div>
      </div>

      {/* Features Grid */}
      <div className="container-custom py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature) => (
            <Link
              key={feature.title}
              to={feature.link}
              className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              <div className={`p-6 bg-gradient-to-r ${feature.color}`}>
                <div className="text-4xl">{feature.icon}</div>
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-gray-500 text-sm">{feature.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Announcements */}
      {announcements.length > 0 && (
        <div className="container-custom py-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">📢 Latest Announcements</h2>
            <Link to="/announcements" className="text-primary hover:underline text-sm">
              View all →
            </Link>
          </div>
          <div className="space-y-4">
            {announcements.map((announcement) => (
              <Link
                key={announcement.id}
                to={`/announcements/${announcement.id}`}
                className="block bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition"
              >
                <h3 className="font-semibold text-lg">{announcement.title}</h3>
                <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                  {announcement.content.replace(/<[^>]*>/g, '').substring(0, 150)}
                </p>
                <span className="text-xs text-gray-400 mt-2 block">
                  {new Date(announcement.created_at).toLocaleDateString()}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Stats Section */}
      <div className="container-custom py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-6 text-center shadow-sm">
            <div className="text-3xl font-bold text-primary">📝</div>
            <p className="text-sm text-gray-500 mt-1">Create Todos</p>
          </div>
          <div className="bg-white rounded-xl p-6 text-center shadow-sm">
            <div className="text-3xl font-bold text-secondary">👍</div>
            <p className="text-sm text-gray-500 mt-1">Vote & Engage</p>
          </div>
          <div className="bg-white rounded-xl p-6 text-center shadow-sm">
            <div className="text-3xl font-bold text-green-500">📚</div>
            <p className="text-sm text-gray-500 mt-1">Join Library</p>
          </div>
          <div className="bg-white rounded-xl p-6 text-center shadow-sm">
            <div className="text-3xl font-bold text-orange-500">📢</div>
            <p className="text-sm text-gray-500 mt-1">Stay Updated</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
